import { useMutation } from "@tanstack/react-query";
import { useCanAccess, useDataProvider, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { StatusPill } from "../design/StatusPill";
import { HELP_CLASS } from "../guardias/guardiasShared";
import type { CrmDataProvider } from "../providers/types";
import type { WhatsAppNumberHealth } from "../types";

const NUMBERS = [
  { key: "inbox", label: "crm.whatsapp.health.inbox" },
  { key: "alerts", label: "crm.whatsapp.health.alerts" },
] as const;

const HealthRow = ({
  label,
  health,
}: {
  label: string;
  health: WhatsAppNumberHealth;
}) => {
  const translate = useTranslate();
  // Only the Meta code and message exactly as the function returns them; never a number or id.
  const text =
    health.status === "OK"
      ? translate("crm.whatsapp.health.connected")
      : health.status === "sin_configuracion"
        ? translate("crm.whatsapp.health.not_configured")
        : translate("crm.whatsapp.health.meta_error", {
            code: health.code ?? "-",
            message: health.message ?? "-",
          });
  return (
    <li className="space-y-1" data-testid="whatsapp-health-row">
      <p className="text-base font-semibold">{label}</p>
      <div role="status">
        <StatusPill
          tone={health.status === "OK" ? "ok" : "not-ok"}
          text={text}
        />
      </div>
    </li>
  );
};

/**
 * Almikia Epic EW3 (TASK-096): the Direccion-only 'Probar conexión de WhatsApp' card. One press
 * is one call to `checkWhatsAppHealth()` (no polling). Per number ('Bandeja de clientes' and
 * 'Avisos internos', never the number or an id) it shows 'Conectado', the Meta code and message,
 * or 'Falta configurar WhatsApp'; a transport / auth failure shows a generic error. The card
 * renders nothing for any other role (or an identity without one); the function gates too.
 */
export const WhatsAppHealthCard = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const access = useCanAccess({
    resource: "whatsapp_settings",
    action: "list",
  });
  const check = useMutation({
    mutationFn: () => dataProvider.checkWhatsAppHealth(),
  });

  if (access.isPending || !access.canAccess) return null;

  const result = check.data;
  const needsRetry =
    check.isError ||
    (result !== undefined &&
      (result.inbox.status !== "OK" || result.alerts.status !== "OK"));

  return (
    <Card data-testid="whatsapp-health-card">
      <CardContent className="space-y-3 p-4">
        <h2 className="text-lg font-semibold">
          {translate("crm.whatsapp.health.title")}
        </h2>
        <p className={HELP_CLASS}>{translate("crm.whatsapp.health.help")}</p>
        {check.isError ? (
          <p role="alert" className="text-base text-destructive">
            {translate("crm.whatsapp.health.error")}
          </p>
        ) : null}
        {result && !check.isPending ? (
          <ul className="space-y-3">
            {NUMBERS.map(({ key, label }) => (
              <HealthRow
                key={key}
                label={translate(label)}
                health={result[key]}
              />
            ))}
          </ul>
        ) : null}
        <Button
          type="button"
          className="min-h-11 w-full text-base sm:w-auto"
          disabled={check.isPending}
          onClick={() => check.mutate()}
        >
          {check.isPending
            ? translate("crm.whatsapp.health.checking")
            : needsRetry
              ? translate("crm.whatsapp.health.retry")
              : translate("crm.whatsapp.health.button")}
        </Button>
      </CardContent>
    </Card>
  );
};
